import React, { useState, useEffect } from "react";
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Button,
  Loader,
  Stack,
  Select,
  Divider,
} from "@mantine/core";
import { useNavigate, useLocation } from "react-router-dom";
import useProfile from "../../hooks/profile";
import { useCurrentUser } from "../../hooks/currentUser";
import queryString from "query-string";
import PuzzlePreviewCard from "./PuzzlePreviewCard";

export default function Profile() {
  const { currentUser, refetchCurrentUser } = useCurrentUser();
  const { characters, loading, activateCharacter } = useProfile({
    currentUser,
    refetchCurrentUser,
  });

  const [selectedCharacterId, setSelectedCharacterId] = useState(
    currentUser?.activeCharacter?.id ?? null,
  );

  const navigate = useNavigate();
  const location = useLocation();
  const needsActiveCharacterReferer = queryString.parse(
    location.search,
  )?.needsActiveCharacterReferer;
  useEffect(() => {
    const hasNavigated = location.state?.redirectedFromCharacterPrompt;
    console.log(currentUser?.activeCharacter);
    if (currentUser?.activeCharacter) {
      const isCampaignCharacter = !!currentUser.activeCharacter.campaignId;
      const isCampaignRedirect =
        typeof needsActiveCharacterReferer === "string" &&
        needsActiveCharacterReferer.includes("/campaigns");
      const isInCampaign =
        typeof needsActiveCharacterReferer === "string" &&
        needsActiveCharacterReferer.includes(
          currentUser.activeCharacter.campaignId,
        );

      if (
        needsActiveCharacterReferer &&
        isCampaignCharacter &&
        isCampaignRedirect &&
        isInCampaign &&
        !hasNavigated
      ) {
        navigate(needsActiveCharacterReferer, {
          replace: true,
          state: { redirectedFromCharacterPrompt: true },
        });
      } else if (needsActiveCharacterReferer) {
        const cleanSearch = { ...queryString.parse(location.search) };
        delete cleanSearch.needsActiveCharacterReferer;

        const newQuery = queryString.stringify(cleanSearch);
        const newPath = `${location.pathname}${newQuery ? `?${newQuery}` : ""}`;
        navigate(newPath, { replace: true });
      }
    }
  }, [currentUser?.activeCharacter]);

  const handleSave = async () => {
    await activateCharacter(selectedCharacterId ?? null);
  };

  if (loading || !currentUser) {
    return (
      <Container>
        <Group position="center" mt="xl">
          <Loader />
        </Group>
      </Container>
    );
  }

  return (
    <Container size="sm" mt="xl">
      <Title order={2}>User Profile</Title>
      <Stack p="xs">
        <Text>
          <strong>Name:</strong> {currentUser.name}
        </Text>
        <Text>
          <strong>Email:</strong> {currentUser.email}
        </Text>
      </Stack>
      <Divider />
      <Title order={5} m="sm">
        Active Character
      </Title>
      <Stack p="xs">
        {needsActiveCharacterReferer && (
          <Card
            shadow="sm"
            padding="md"
            radius="md"
            withBorder
            style={{ backgroundColor: "#fff3cd" }}
          >
            <Text c="yellow.9" weight={500}>
              This page requires you to select an active character.
            </Text>
            <Text size="sm">
              Once selected, you'll be redirected back to continue where you
              left off at {needsActiveCharacterReferer} if your character exists
              in that campaign.
            </Text>
          </Card>
        )}
        <Select
          data={characters.map((char) => ({
            value: String(char.id),
            label: `${char.name} (${char.campaignId})`,
          }))}
          placeholder="Select a character"
          value={selectedCharacterId?.toString() ?? null}
          onChange={(value) =>
            setSelectedCharacterId(value ? parseInt(value) : null)
          }
          clearable
        />

        <Group position="right">
          <Button onClick={handleSave} disabled={loading}>
            Save Active Character
          </Button>
        </Group>

        {currentUser.activeCharacter && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text>
              <strong>Name:</strong> {currentUser.activeCharacter.name}
            </Text>
            <Text>
              <strong>Campaign:</strong>{" "}
              {currentUser.activeCharacter.campaignId}
            </Text>
          </Card>
        )}
      </Stack>
      <PuzzlePreviewCard
        activeCharacterId={currentUser.activeCharacter?.id ?? null}
      />
    </Container>
  );
}
